// Redux slice for managing facilities status loading, data, and errors
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getFacilitiesStatus, getFacilityById } from './facility.thunk';
import type { ProductionLine } from '../utils/types';

// State shape for the facilities status feature
interface FacilityState {
  loading: boolean;
  facilitiesStatusData?: ProductionLine[];
  facilityDetailData?: ProductionLine | null;
  error?: string | null;
}

// Initial state for the facilities status slice
const initialState: FacilityState = {
  loading: false,
  facilitiesStatusData: undefined,
  facilityDetailData: null,
  error: null,
};

const facilitiesSlice = createSlice({
  name: 'facilities',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Handle getFacilitiesStatus pending state
      .addCase(getFacilitiesStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })

      // Handle getFacilitiesStatus fulfilled state
      .addCase(
        getFacilitiesStatus.fulfilled,
        (state, action: PayloadAction<ProductionLine[]>) => {
          state.facilitiesStatusData = action.payload;
          state.loading = false;
        }
      )

      // Handle getFacilitiesStatus rejected state
      .addCase(getFacilitiesStatus.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch facilities status';
      })
      // Handle getFacilityById pending state
      .addCase(getFacilityById.pending, state => {
        state.loading = true;
        state.error = null;
      })

      // Handle getFacilityById fulfilled state
      .addCase(
        getFacilityById.fulfilled,
        (state, action: PayloadAction<ProductionLine | null>) => {
          state.facilityDetailData = action.payload;
          state.loading = false;
        }
      )

      // Handle getFacilityById rejected state
      .addCase(getFacilityById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch facility by ID';
      });
  },
});

export default facilitiesSlice.reducer;
